import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export default function NotFoundPage() {
  const { t } = useTranslation('notFound')

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center relative overflow-hidden">
      {/* 背景装饰元素 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* 浮动的圆圈 */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-primary-200/30 rounded-full blur-2xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary-200/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-primary-300/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }} />
        
        {/* 装饰性星星 */}
        <div className="absolute top-32 right-32 w-2 h-2 bg-primary-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-40 left-40 w-1.5 h-1.5 bg-secondary-400 rounded-full animate-ping" style={{ animationDelay: '1.5s' }} />
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-primary-500 rounded-full animate-ping" style={{ animationDelay: '2.5s' }} />
      </div>

      <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
        {/* 404 数字 - 大而醒目 */}
        <div className="mb-8">
          <h1 className="text-9xl md:text-[12rem] font-bold bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-600 bg-clip-text text-transparent leading-none animate-[fadeIn_0.6s_ease-out]">
            404
          </h1>
        </div>

        {/* 可爱的图标 */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            {/* 主图标 - 可爱的搜索/迷失图标 */}
            <svg
              className="w-32 h-32 text-primary-400 animate-bounce"
              style={{ animationDuration: '2s' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {/* 装饰性问号 */}
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary-400 rounded-full flex items-center justify-center animate-spin" style={{ animationDuration: '3s' }}>
              <span className="text-white text-lg font-bold">?</span>
            </div>
          </div>
        </div>

        {/* 标题和描述 */}
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 animate-[fadeIn_0.8s_ease-out]">
          {t('title', 'Oops! Page Not Found')}
        </h2>
        <p className="text-lg text-gray-600 mb-8 animate-[fadeIn_1s_ease-out]">
          {t('description', "The page you're looking for seems to have wandered off into the digital void. Don't worry, let's get you back on track!")}
        </p>

        {/* 操作按钮 */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-[fadeIn_1.2s_ease-out]">
          <Link
            to="/"
            className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-primary-300/50 hover:scale-105 active:scale-95 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            {t('goHome', 'Go Home')}
          </Link>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-white/80 backdrop-blur-sm border-2 border-primary-300 text-primary-600 font-medium rounded-xl transition-all duration-300 hover:bg-primary-50 hover:border-primary-400 hover:scale-105 active:scale-95 flex items-center gap-2 shadow-sm hover:shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('goBack', 'Go Back')}
          </button>
        </div>

        {/* 可爱的提示 */}
        <div className="mt-12 p-4 bg-primary-50/50 backdrop-blur-sm border border-primary-200/50 rounded-xl inline-block animate-[fadeIn_1.4s_ease-out]">
          <p className="text-sm text-gray-600 flex items-center gap-2">
            <span className="text-2xl">🔍</span>
            <span>{t('tip', 'Tip: Check the URL or use the navigation menu to find what you need.')}</span>
          </p>
        </div>
      </div>
    </div>
  )
}

